import React, { useState } from 'react';
import ToolPage from '@/components/ToolPage';
import { Field, inputClass } from '@/components/calcUi';
import { getToolBySlug } from '@/lib/tools';
import { formatNumber } from '@/lib/currency';

const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null);

function diffYMD(from, to) {
  if (!from || !to || isNaN(from) || isNaN(to)) return null;
  let y = to.getFullYear() - from.getFullYear();
  let m = to.getMonth() - from.getMonth();
  let d = to.getDate() - from.getDate();
  if (d < 0) {
    m -= 1;
    const prev = new Date(to.getFullYear(), to.getMonth(), 0);
    d += prev.getDate();
  }
  if (m < 0) { y -= 1; m += 12; }
  const days = Math.floor((to - from) / 86400000);
  return { years: y, months: m, days: d, totalDays: days };
}

// ========================= Age Calculator =========================
export function AgeCalculator() {
  const tool = getToolBySlug('age-calculator');
  const today = new Date().toISOString().slice(0, 10);
  const [dob, setDob] = useState('2000-01-15');
  const [ref, setRef] = useState(today);
  const from = toDate(dob), to = toDate(ref);
  const r = from && to && from <= to ? diffYMD(from, to) : null;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Enter a date of birth to see an exact age, plus useful totals in weeks and days.</p>}
      formula={<>Years, months, and days between two dates, with month lengths accounted for.</>}
      examples={[
        'Someone born 1990-03-12, on 2025-06-01, is 35 years, 2 months, 20 days old.',
        'A baby born 2024-06-15, on 2025-06-15, is exactly 1 year (365 days) old.',
      ]}
      faqs={[
        { q: 'Does this handle leap years?', a: 'Yes. Month and day calculations respect real calendar month lengths, including 29 February in leap years.' },
        { q: 'Why does the answer differ from a simple year subtraction?', a: 'Subtracting years alone ignores months and days. For legal-age or birthday-not-yet-reached cases, a full YMD calculation is more accurate.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label="Date of birth" htmlFor="age-dob">
            <input id="age-dob" data-testid="age-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Reference date" htmlFor="age-ref">
            <input id="age-ref" data-testid="age-ref" type="date" value={ref} onChange={(e) => setRef(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Exact age</div>
          {r ? (
            <>
              <div className="text-4xl font-bold text-emerald-700" data-testid="age-result">
                {r.years} <span className="text-lg text-slate-500 font-medium">yr</span> {r.months} <span className="text-lg text-slate-500 font-medium">mo</span> {r.days} <span className="text-lg text-slate-500 font-medium">d</span>
              </div>
              <div className="mt-3 text-sm text-slate-600 space-y-1">
                <div>Total days: <strong>{formatNumber(r.totalDays, 0)}</strong></div>
                <div>Total weeks: <strong>{formatNumber(r.totalDays / 7, 1)}</strong></div>
              </div>
            </>
          ) : <div className="text-slate-400 text-lg">Pick a date of birth on or before the reference date.</div>}
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Date Calculator =========================
export function DateCalculator() {
  const tool = getToolBySlug('date-calculator');
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [dir, setDir] = useState('add');
  const [days, setDays] = useState('0');
  const [weeks, setWeeks] = useState('0');
  const [months, setMonths] = useState('0');
  const [years, setYears] = useState('0');

  const d = toDate(start);
  let out = null;

  const addYearsMonthsClamped = (date, yearsToAdd, monthsToAdd) => {
    const originalDay = date.getDate();

    // Calculate the target year/month first.
    // Starting from day 1 prevents JavaScript's date rollover.
    const targetMonthIndex =
      date.getMonth() + yearsToAdd * 12 + monthsToAdd;

    const target = new Date(
      date.getFullYear(),
      targetMonthIndex,
      1
    );

    // Last valid day of the target month.
    const lastDay = new Date(
      target.getFullYear(),
      target.getMonth() + 1,
      0
    ).getDate();

    // Preserve the original day when possible,
    // otherwise clamp to the last valid day.
    target.setDate(Math.min(originalDay, lastDay));

    return target;
  };

  if (d && !isNaN(d)) {
    const sign = dir === 'add' ? 1 : -1;

    const yearValue = Number.parseInt(years, 10) || 0;
    const monthValue = Number.parseInt(months, 10) || 0;
    const weekValue = Number.parseInt(weeks, 10) || 0;
    const dayValue = Number.parseInt(days, 10) || 0;

    const yearsToAdd = sign * yearValue;
    const monthsToAdd = sign * monthValue;
    const daysToAdd =
      sign * (weekValue * 7 + dayValue);

    let nd = addYearsMonthsClamped(
      d,
      yearsToAdd,
      monthsToAdd
    );

    if (daysToAdd !== 0) {
      nd.setDate(nd.getDate() + daysToAdd);
    }

    out = nd;
  }

  return (
    <ToolPage
      tool={tool}
      intro={
        <p>
          Add or subtract days, weeks, months, and years from a starting date.
        </p>
      }
      formula={
        <>
          Result = Start ± years/months ± weeks/days, with calendar month
          lengths respected.
        </>
      }
      examples={[
        'Start 2025-01-01, add 90 days → 2025-04-01.',
        'Start 2025-05-31, subtract 1 month → 2025-04-30.',
      ]}
      faqs={[
        {
          q: 'How are month lengths handled?',
          a: 'If the target month does not contain the original day, the calculator uses the last valid day of that month. For example, May 31 minus 1 month becomes April 30.',
        },
        {
          q: 'Does it handle leap years?',
          a: 'Yes. Leap years and February 29 are handled according to the calendar.',
        },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Field label="Start date" htmlFor="date-start">
            <input
              id="date-start"
              data-testid="date-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label="Direction">
            <div className="flex gap-2">
              {['add', 'subtract'].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDir(k)}
                  data-testid={`date-dir-${k}`}
                  className={`flex-1 h-11 rounded-lg text-sm font-medium ${
                    dir === k
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                >
                  {k === 'add' ? 'Add' : 'Subtract'}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-4 gap-3">
            {[
              { k: 'years', v: years, set: setYears, label: 'Years' },
              { k: 'months', v: months, set: setMonths, label: 'Months' },
              { k: 'weeks', v: weeks, set: setWeeks, label: 'Weeks' },
              { k: 'days', v: days, set: setDays, label: 'Days' },
            ].map((f) => (
              <Field
                key={f.k}
                label={f.label}
                htmlFor={`date-${f.k}`}
              >
                <input
                  id={`date-${f.k}`}
                  data-testid={`date-${f.k}`}
                  type="number"
                  value={f.v}
                  onChange={(e) => f.set(e.target.value)}
                  className={inputClass}
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
            Result date
          </div>

          <div
            className="text-4xl font-bold text-emerald-700"
            data-testid="date-result"
          >
            {out
              ? out.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long',
                })
              : '—'}
          </div>
        </div>
      </div>
    </ToolPage>
  );
}
// ========================= Time Duration =========================
export function TimeCalculator() {
  const tool = getToolBySlug('time-calculator');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:30');
  const parse = (s) => { const [h, m] = s.split(':').map(Number); if (isNaN(h) || isNaN(m)) return null; return h * 60 + m; };
  const s = parse(start), e = parse(end);
  const valid = s !== null && e !== null;
  let mins = valid ? (e - s + 24 * 60) % (24 * 60) : NaN;
  const hh = Math.floor(mins / 60), mm = mins % 60;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Get the exact duration between a start and end time on the same day. If the end time is earlier than the start, we assume it rolls over to the next day.</p>}
      formula={<>Duration (minutes) = (End − Start + 1440) mod 1440</>}
      examples={[
        '09:00 to 17:30 = 8 hours 30 minutes (a standard workday).',
        '22:15 to 06:45 = 8 hours 30 minutes (overnight shift).',
      ]}
      faqs={[
        { q: 'Does this handle overnight shifts?', a: 'Yes — if the end time is earlier than the start time, the calculator rolls to the next day.' },
        { q: 'Can I subtract a break?', a: 'Not yet. Subtract the break minutes from the total manually, or watch this space for an update.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label="Start time" htmlFor="time-start">
            <input id="time-start" data-testid="time-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} className={inputClass} />
          </Field>
          <Field label="End time" htmlFor="time-end">
            <input id="time-end" data-testid="time-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Duration</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="time-result">
            {valid ? `${hh}h ${mm}m` : '—'}
          </div>
          {valid && (
            <div className="mt-3 text-sm text-slate-600">
              Decimal hours: <strong>{formatNumber(mins / 60, 2)}</strong> · Total minutes: <strong>{mins}</strong>
            </div>
          )}
        </div>
      </div>
    </ToolPage>
  );
}
